import clsx from 'clsx'
import { useState, useEffect, Suspense, useTransition, useMemo } from 'react'
import { Group, Loader, Stack } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconStar, IconUser, IconUsers } from '@tabler/icons-react'
import Stat from '../../stories/Stat/Stat'
import ClaireCard from '../../stories/ClaireCard/ClaireCard'
import classes from './PerformanceComponents.module.css'
import PerformancePulse from './PerformancePulse'
import ProgressBox from '../../widgets/components/ProgressBox'
import { useCompetitionStats } from '../api'
import { useCompetitionId } from '../../common/hooks'
import { useMonthKPIs } from '../../widgets/hooks'
import { getInsight } from '../insights'
import { WidgetContextProvider } from '../../widgets/providers'
import { CompareSubject } from '../../compare/Compare'
import SubjectSelector from '../../stories/SubjectSelector/SubjectSelector'
import { useAllPlayers } from '../../players/api'
import { CompetitionTeam } from '../../compare/api'
import KpiTabsContainer from '../../widgets/components/KpiTabsContainer'
import PerformanceGraph from './PerformanceGraph'
import ViewSelector, {
  OptionKey,
} from '../../stories/ViewSelector/ViewSelector'
import { Block } from '../../common/components/metrics/PerformanceSection'
import { Dropdown } from '../../common/components/dropdown/Dropdown'
import { useWidgetContext } from '../../widgets/providers'
import { useMetrics } from '../../widgets/api'

export default function PerformanceSummary() {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const competition_id = useCompetitionId()
  const { data: month } = useMonthKPIs(competition_id, true)
  const insightResult = getInsight(month)
  const { data: stats } = useCompetitionStats({ competition_id })
  const [subject, setSubject] = useState<CompareSubject>(null)

  // State for managing period selection in Performance Pulse
  const [periodType, setPeriodType] = useState<'total' | 'year' | 'month'>(
    'total'
  )
  const [period, setPeriod] = useState<string>('2025-01-01')
  const [isPending, startTransition] = useTransition()

  // State for view selector in Total Performance section
  const [view, setView] = useState<OptionKey>('line')
  const [component, setComponent] = useState<string>('percent')

  const handlePeriodChange = (newPeriodType: string, newPeriod: string) => {
    startTransition(() => {
      setPeriodType(newPeriodType as 'total' | 'year' | 'month')
      setPeriod(newPeriod)
    })
  }

  return (
    <div className={classes.summary_wrapper}>
      <div
        className={clsx(classes.total_container, {
          [classes.total_container_mobile]: !isDesktop,
        })}
      >
        <div
          className={clsx(classes.stat_container, {
            [classes.stat_container_mobile]: !isDesktop,
          })}
        >
          <Stat
            title="Avg. Rating"
            value={stats.overall_rating ? stats.overall_rating.toFixed(2) : '-'}
            background="white"
            icon={IconStar}
          />
          <Stat
            title="No. of Teams"
            value={stats.num_teams}
            background="white"
            icon={IconUsers}
          />
          <Stat
            title="No. of Players"
            value={stats.num_players}
            background="white"
            icon={IconUser}
          />
        </div>
        <ClaireCard title="Insights" text={insightResult.text} />
        {isDesktop && (
          <div className={classes.stat_cards}>
            <WidgetContextProvider
              context={{
                period_type: 'month',
                period: month.current_period,
                subject_type: 'overall',
              }}
            >
              <WidgetContextProvider
                context={{ metric: insightResult?.topPerformer?.key }}
              >
                <ProgressBox id="insights0" />
              </WidgetContextProvider>
              <WidgetContextProvider
                context={{ metric: insightResult?.bottomPerformer?.key }}
              >
                <ProgressBox id="insights1" />
              </WidgetContextProvider>
            </WidgetContextProvider>
          </div>
        )}
      </div>

      <SubjectFilter subject={subject} setSubject={setSubject} />

      <div style={{ position: 'relative' }}>
        <WidgetContextProvider
          context={{
            period_type: periodType,
            period: period,
            subject_type: subject ? subject.type : 'overall',
            subject: subject ? subject.id : 0,
          }}
        >
          <Suspense
            fallback={
              <div
                className={clsx(classes.performance_pulse_container, {
                  [classes.performance_pulse_container_mobile]: !isDesktop,
                })}
                style={{
                  opacity: 0.5,
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px',
                  }}
                >
                  <Loader size="md" />
                </div>
              </div>
            }
          >
            <div
              style={{
                opacity: isPending ? 0.5 : 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <PerformancePulse
                players={stats.num_players}
                teams={stats.num_teams}
                onPeriodChange={handlePeriodChange}
              />
            </div>
          </Suspense>
        </WidgetContextProvider>
      </div>

      <div>
        <KpiTabsContainer
          title="Total Performance"
          id={'analysis_kpi_tabs'}
          subheading={
            <ViewControls
              view={view}
              setView={setView}
              component={component}
              setComponent={setComponent}
            />
          }
        >
          <WidgetContextProvider
            context={{
              subject_type: subject ? subject.type : 'overall',
              subject: subject ? subject.id : 0,
            }}
          >
            <Suspense
              fallback={
                <Stack w="full" h="400px" justify="center" align="center">
                  <Loader color="#0B6B7B" />
                </Stack>
              }
            >
              <PerformanceGraph view={view} component={component} />
            </Suspense>
          </WidgetContextProvider>
        </KpiTabsContainer>
      </div>
    </div>
  )
}

function ViewControls({
  view,
  setView,
  component,
  setComponent,
}: {
  view: OptionKey
  setView: (v: OptionKey) => void
  component: string
  setComponent: (v: string) => void
}) {
  const ctx = useWidgetContext()
  const { data: metrics } = useMetrics()
  const metric = metrics?.find((m) => m.key === ctx.metric)
  const isPercent = metric?.is_percent_metric ?? false

  useEffect(() => {
    if (isPercent && view === 'waterfall') {
      setView('bar')
    }
  }, [isPercent, view, setView])

  return (
    <Block>
      <Group
        wrap="nowrap"
        align="center"
        justify="flex-end"
        gap="xs"
        pr="md"
        pl="md"
      >
        <ViewSelector
          value={view}
          onChange={setView}
          enabled={isPercent ? ['line', 'bar'] : ['line', 'bar', 'waterfall']}
        />
        <Dropdown
          placeholder="Select component"
          disabled={view === 'waterfall'}
          data={[
            { label: 'Target (%)', value: 'percent' },
            { label: 'Actual', value: 'actual' },
          ]}
          styles={{
            root: { maxWidth: '150px' },
            input: { backgroundColor: 'transparent' },
          }}
          value={component}
          onChange={(value) => setComponent(value || 'percent')}
        />
      </Group>
    </Block>
  )
}

function SubjectFilter(props: {
  subject: CompareSubject
  setSubject: React.Dispatch<React.SetStateAction<CompareSubject>>
}) {
  const [opened, { open, close }] = useDisclosure(false)
  const competition_id = useCompetitionId()

  // Fetch teams and players data
  const { data: players } = useAllPlayers({
    competition_id: Number(competition_id),
  })

  const teamsWithPlayers: CompetitionTeam[] = useMemo(() => {
    const teamsMap = new Map<number, CompetitionTeam>()

    players.items.forEach((player) => {
      if (!teamsMap.has(player.team_id)) {
        teamsMap.set(player.team_id, {
          team_id: player.team_id,
          team_name: player.team_name,
          team_logo: player.team_logo,
          players: [],
        })
      }

      teamsMap.get(player.team_id)!.players.push({
        player_id: player.player_id,
        user_id: player.user_id,
        player_name: player.name,
        player_photo: player.photo,
      })
    })

    return Array.from(teamsMap.values())
  }, [players])

  return (
    <SubjectSelector
      opened={opened}
      open={open}
      close={close}
      teamWithPlayers={teamsWithPlayers}
      subject={props.subject}
      setSubject={props.setSubject}
    />
  )
}
